import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Bell, Vault, CheckCircle, Warning } from "@phosphor-icons/react";
import Header from "../components/Header";
import FeedbackCTA from "../components/FeedbackCTA";
import HeroLiveFeed from "../components/HeroLiveFeed";
import useAbTest from "../lib/useAbTest";
import { RETAILER_LANDINGS } from "../lib/retailerLandingData";

/**
 * /track/:retailerSlug — a retailer-specific landing page that captures
 * long-tail queries like "pb tech price drop refund".
 *
 * Sets <title> + <meta name="description"> + canonical for each retailer
 * directly on document (no react-helmet dep). Google JS-renders these pages
 * during crawl, so client-side meta updates index correctly.
 *
 * Shares the LandingHeader/Footer feel but is content-focused — single
 * column, no animated trio, fast read, clear single CTA.
 */
export default function RetailerLanding() {
  const { retailerSlug } = useParams();
  const data = RETAILER_LANDINGS[retailerSlug];

  // A/B test reused so we can compare retailer-landing CTA performance vs hero CTA.
  const { variant: ctaVariant, track } = useAbTest("retailer_cta_v1", ["control", "wishlist"]);
  const ctaLabel = ctaVariant === "wishlist"
    ? `Start tracking my ${data?.name ?? ""} purchases`.trim()
    : "Start protecting my purchases";

  // SEO: set page title + meta on mount, restore on unmount.
  useEffect(() => {
    if (!data) return;
    const prevTitle = document.title;
    document.title = data.metaTitle;

    const setMeta = (selector, attr, value) => {
      let tag = document.head.querySelector(selector);
      const created = !tag;
      if (!tag) {
        tag = document.createElement("meta");
        const [, kind] = selector.match(/\[(.*?)=/) || [];
        const [, name] = selector.match(/="(.*?)"/) || [];
        if (kind && name) tag.setAttribute(kind, name);
        document.head.appendChild(tag);
      }
      const prev = tag.getAttribute(attr);
      tag.setAttribute(attr, value);
      return () => {
        if (created) tag.remove();
        else if (prev !== null) tag.setAttribute(attr, prev);
      };
    };

    const restoreDesc = setMeta('meta[name="description"]', "content", data.metaDescription);
    const restoreOgTitle = setMeta('meta[property="og:title"]', "content", data.metaTitle);
    const restoreOgDesc = setMeta('meta[property="og:description"]', "content", data.metaDescription);
    const restoreOgUrl = setMeta('meta[property="og:url"]', "content", `https://priceguard.nz/track/${data.slug}`);

    // Canonical link
    let canonical = document.head.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute("href");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://priceguard.nz/track/${data.slug}`);

    return () => {
      document.title = prevTitle;
      restoreDesc();
      restoreOgTitle();
      restoreOgDesc();
      restoreOgUrl();
      if (prevCanonical) canonical.setAttribute("href", prevCanonical);
    };
  }, [data]);

  if (!data) {
    return <Navigate to="/" replace />;
  }

  const confirmed = data.policyConfidence === "confirmed";

  return (
    <div className="min-h-screen bg-white text-[#0a0a0a]">
      <Header />

      {/* HERO */}
      <section className="border-b border-[#e4e4e7] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-12 lg:pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <div className="lg:col-span-7 pg-fade">
            <div className="inline-flex items-center gap-2 mb-6 border border-black px-3 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A] pg-pulse" />
              <span className="pg-overline">Live · {data.name} tracking</span>
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              Never overpay<br/>
              at {data.name}<span className="text-[#002FA7]">.</span>
            </h1>
            <p className="text-base sm:text-lg font-semibold text-[#0a0a0a] mt-6 max-w-xl">
              {data.summary}
            </p>

            {/* Honest policy badge — confirmed vs unconfirmed */}
            <div className="mt-6 flex items-start gap-3 border border-[#e4e4e7] bg-[#f7f7f8] p-4 max-w-xl" data-testid="retailer-policy-badge">
              {confirmed ? (
                <CheckCircle size={22} weight="fill" color="#16A34A" className="mt-0.5 flex-shrink-0" />
              ) : (
                <Warning size={22} weight="fill" color="#D97706" className="mt-0.5 flex-shrink-0" />
              )}
              <div className="text-sm leading-snug">
                <div className="font-bold">
                  {confirmed
                    ? `${data.policyDays}-day window, published as ${data.policyName}`
                    : `No published window, we ask politely on your behalf`}
                </div>
                <div className="text-[#52525b] mt-1">
                  {confirmed
                    ? <>The {data.name} policy is publicly listed. <a href={data.policyUrl} target="_blank" rel="noopener noreferrer" className="underline">Read it here</a>.</>
                    : <>Our default window: <strong>{data.policyDays} days</strong>. <a href={data.policyUrl} target="_blank" rel="noopener noreferrer" className="underline">{data.name}'s policy page</a> · the decision is always theirs.</>}
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Link
                to="/signup"
                onClick={() => track("ab_test_click_signup", { cta_location: `retailer_${data.slug}` })}
                className="bg-[#002FA7] text-white font-bold px-7 py-4 inline-flex items-center gap-3 hover:bg-[#00207A] transition-colors"
                data-testid="retailer-cta-signup"
                data-ab-variant={ctaVariant}
              >
                {ctaLabel} <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/login" className="border border-black font-bold px-7 py-4 inline-flex items-center justify-center hover:bg-black hover:text-white transition-colors">
                I already have an account
              </Link>
            </div>
            <p className="text-xs font-mono-pg text-[#52525b] mt-6 uppercase tracking-widest">
              Pro free for everyone right now · Unlimited tracking · No card needed
            </p>
          </div>

          {/* Reuse the live feed — same heartbeat as the main landing page. */}
          <div className="lg:col-span-5">
            <HeroLiveFeed />
          </div>
        </div>
      </section>

      {/* WHAT PRICEGUARD DOES FOR THIS RETAILER */}
      <section className="border-b border-[#e4e4e7] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <h2 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight max-w-2xl mb-12">
            What PriceGuard does for your {data.name} purchases.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7]" data-testid="retailer-bullets">
            {data.bullets.map((b, i) => {
              const Icon = [Bell, ShieldCheck, Vault][i] || Bell;
              return (
                <div key={i} className="bg-white p-6 lg:p-8 pg-lift">
                  <Icon size={28} weight="duotone" color="#002FA7" />
                  <h3 className="font-heading text-lg font-bold tracking-tight mt-5">{b.title}</h3>
                  <p className="text-[#52525b] text-sm leading-relaxed mt-2">{b.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — short 3-step block */}
      <section className="border-b border-[#e4e4e7] bg-[#f7f7f8]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <h2 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight max-w-2xl mb-12">
            Three steps. Five minutes.
          </h2>
          <ol className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
            {[
              { n: "01", t: `Paste the ${data.name} product URL`, b: `Or upload your receipt, we'll read it with AI.` },
              { n: "02", t: "We watch every day", b: `From ${data.policyDays === 7 ? "the day you buy" : "the moment you sign up"} until the window closes.` },
              { n: "03", t: "We draft the email", b: `Politely. Honestly. Anchored in ${data.name}'s published terms.` },
            ].map((s) => (
              <li key={s.n} className="bg-white p-6 lg:p-8">
                <div className="font-mono-pg text-xs tracking-widest text-[#002FA7] font-bold">STEP {s.n}</div>
                <div className="font-heading text-lg font-bold tracking-tight mt-3">{s.t}</div>
                <div className="text-[#52525b] text-sm leading-relaxed mt-2">{s.b}</div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* OTHER RETAILERS — cross-link for SEO + UX */}
      <section className="border-b border-[#e4e4e7] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
          <h2 className="font-heading text-2xl lg:text-3xl font-extrabold tracking-tight mb-6">
            Shopping elsewhere too?
          </h2>
          <p className="text-[#52525b] text-base max-w-2xl mb-8">
            PriceGuard works with the major NZ electronics retailers. Track all of them from one dashboard, free.
          </p>
          <div className="flex flex-wrap gap-3" data-testid="retailer-cross-links">
            {Object.values(RETAILER_LANDINGS)
              .filter((r) => r.slug !== data.slug)
              .map((r) => (
                <Link
                  key={r.slug}
                  to={`/track/${r.slug}`}
                  className="border border-black px-4 py-2 text-sm font-semibold bg-white pg-lift-sm"
                  data-testid={`cross-link-${r.slug}`}
                >
                  {r.name} →
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-b border-[#e4e4e7] bg-[#0a0a0a] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 text-center">
          <h2 className="font-heading text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight max-w-3xl mx-auto">
            One alert pays for years.<br/>
            <span className="text-white/70">Start watching your {data.name} purchases.</span>
          </h2>
          <Link
            to="/signup"
            onClick={() => track("ab_test_click_signup", { cta_location: `retailer_${data.slug}_footer` })}
            className="inline-flex items-center gap-3 mt-10 bg-white text-black font-bold px-7 py-4 hover:bg-[#f0f0f2] transition-colors"
            data-testid="retailer-cta-footer"
          >
            {ctaLabel} <ArrowRight size={18} weight="bold" />
          </Link>
          <p className="text-xs font-mono-pg text-white/40 mt-6 uppercase tracking-widest">
            Free for everyone during launch · No card needed
          </p>
        </div>
      </section>

      <FeedbackCTA context={`retailer:${data.slug}`} />

      <footer className="py-10 px-6 lg:px-10 border-t border-[#e4e4e7]">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row justify-between gap-6">
          <div className="text-sm">
            <Link to="/" className="font-heading font-bold">PriceGuard NZ</Link>
            <p className="text-xs text-[#52525b] mt-1">Built in Aotearoa. Honest about retailer policies.</p>
          </div>
          <div className="text-xs text-[#52525b] flex flex-wrap gap-4">
            <Link to="/about" className="hover:text-black">About</Link>
            <Link to="/pricing" className="hover:text-black">Pricing</Link>
            <Link to="/faq" className="hover:text-black">FAQ</Link>
            <Link to="/privacy" className="hover:text-black">Privacy</Link>
            <Link to="/terms" className="hover:text-black">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
